import React, { useRef } from 'react'
import { ChevronLeft, ChevronRight, Download, Maximize2, Minimize2, MessageSquarePlus, X } from 'lucide-react'
import { BaseModalV3, Button, HorizontalScroller } from 'components/ui'
import { useMediaFullscreen } from '../../hooks/useMediaFullscreen'
import { useExecutionText } from '../../hooks/useExecutionText'
import { mediaName, videoMedia } from '../../model/media'
import styles from './styles.module.css'

interface IExecutionMediaPreviewProps {
  media: string[]
  index: number
  counts: number[]
  isRtl: boolean
  onChange: (index: number) => void
  onClose: () => void
  onComment?: () => void
  onDownload: () => void
  downloading: boolean
  navigationDisabled: boolean
  onComposerTarget?: (element: HTMLDivElement | null) => void
  commentOverlayOpen: boolean
}

/** Controlled by the same media selection as the result discussion. */
export function ExecutionMediaPreview({
  media,
  index,
  counts,
  isRtl,
  onChange,
  onClose,
  onComment,
  onDownload,
  downloading,
  navigationDisabled,
  onComposerTarget,
  commentOverlayOpen,
}: IExecutionMediaPreviewProps) {
  const t = useExecutionText()
  const fullscreen = useMediaFullscreen()
  const touch = useRef<number | null>(null)
  const url = media[index]
  const move = (delta: number) => {
    if (!navigationDisabled && index + delta >= 0 && index + delta < media.length) onChange(index + delta)
  }

  return (
    <BaseModalV3
      isOpen
      onClose={onClose}
      closeOnEsc={!commentOverlayOpen}
      trapFocus
      closeOnOverlayClick
      contentProps={{
        className: styles.preview,
        dir: isRtl ? 'rtl' : 'ltr',
        'aria-label': t.fullscreen,
        style: { width: '100%', maxWidth: 'none', minWidth: 0, height: '100%', borderRadius: 0, background: '#181b1e' },
        onKeyDown: (event: React.KeyboardEvent) => {
          const target = event.target as HTMLElement
          if (commentOverlayOpen || target.closest('input, textarea, [contenteditable="true"], video, [role="menu"]'))
            return
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault()
            move((event.key === 'ArrowRight') !== isRtl ? 1 : -1)
          }
        },
      }}
      headerProps={{
        className: styles.header,
        style: { background: 'transparent', boxShadow: 'none' },
      }}
      header={
        <>
          <span className={styles.name}>
            {t.file}{' '}
            <bdi>
              {index + 1} / {media.length}
            </bdi>{' '}
            · <bdi>{mediaName(url)}</bdi>
          </span>
          {onComment && (
            <Button variant="icon" onClick={onComment} aria-label={t.commentOnMedia} title={t.commentOnMedia}>
              <MessageSquarePlus size={20} />
            </Button>
          )}
          <Button
            variant="icon"
            onClick={onDownload}
            isLoading={downloading}
            isDisabled={downloading}
            aria-label={t.download}
            title={t.download}
          >
            <Download size={20} />
          </Button>
          {fullscreen.supported && (
            <Button
              variant="icon"
              onClick={fullscreen.toggle}
              aria-label={t.fullscreen}
              title={t.fullscreen}
              aria-pressed={fullscreen.active}
            >
              {fullscreen.active ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
            </Button>
          )}
          <Button variant="icon" onClick={onClose} aria-label={t.close} title={t.close}>
            <X size={22} />
          </Button>
        </>
      }
      bodyProps={{
        style: { padding: 0, background: 'transparent', maxHeight: 'none', height: 'auto', overflow: 'hidden' },
      }}
      body={
        <div
          className={styles.stage}
          onTouchStart={(event) => {
            touch.current = event.touches[0].clientX
          }}
          onTouchEnd={(event) => {
            if (touch.current === null) return
            const delta = touch.current - event.changedTouches[0].clientX
            if (Math.abs(delta) > 45) move(delta > 0 !== isRtl ? 1 : -1)
            touch.current = null
          }}
        >
          {videoMedia(url) ? (
            <video key={url} src={url} controls playsInline preload="metadata" />
          ) : (
            <img key={url} src={url} alt={`${t.file} ${index + 1}`} />
          )}
          {index > 0 && (
            <Button
              className={styles.prev}
              variant="icon"
              isDisabled={navigationDisabled}
              onClick={() => move(-1)}
              aria-label={t.previous}
            >
              {isRtl ? <ChevronRight /> : <ChevronLeft />}
            </Button>
          )}
          {index < media.length - 1 && (
            <Button
              className={styles.next}
              variant="icon"
              isDisabled={navigationDisabled}
              onClick={() => move(1)}
              aria-label={t.next}
            >
              {isRtl ? <ChevronLeft /> : <ChevronRight />}
            </Button>
          )}
        </div>
      }
      footerProps={{
        className: styles.footer,
        style: { padding: undefined, background: 'transparent', display: 'block', overflow: 'visible' },
      }}
      footer={
        <>
          <HorizontalScroller className={styles.thumbnails} fadeSize={24}>
            {media.map((item, i) => (
              <Button
                key={item}
                variant="ghost"
                className="execution-thumbnail"
                aria-pressed={i === index}
                aria-label={`${t.file} ${i + 1} · ${counts[i] || 0} ${t.comments}`}
                isDisabled={navigationDisabled}
                onClick={() => onChange(i)}
              >
                {videoMedia(item) ? (
                  <video src={`${item}#t=0.1`} muted preload="metadata" />
                ) : (
                  <img src={item} alt="" />
                )}
              </Button>
            ))}
          </HorizontalScroller>
          <div ref={onComposerTarget} className={styles.composer} />
        </>
      }
    />
  )
}
